import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Clapperboard, LogOut, Sparkles, UserSquare2, Menu, X, Film,
  SlidersHorizontal, ListChecks,
} from 'lucide-react'
import { api, tokens, refreshTokens } from './api'
import type { User, Workspace, Role } from './types'
import { useRoute, href, go, setActiveWorkspaceRoute, type Route } from './router'
import { Button, Skeleton } from './components/ui'
import { Select } from './components/select'
import { TaskIndicator } from './components/TaskIndicator'
import { Login } from './pages/Login'
import { InvitationAcceptance, PasswordRecovery } from './pages/AuthFlows'
import { AccountSecurity } from './pages/AccountSecurity'
import { Jobs } from './pages/Jobs'
import { JobDetail } from './pages/JobDetail'
import { NewJob } from './pages/NewJob'
import { Settings } from './pages/Settings'
import { WorkspaceOnboarding } from './pages/WorkspaceOnboarding'
import { GettingStarted } from './pages/GettingStarted'
import { PhoneUpload } from './pages/PhoneUpload'
import { cn } from './lib/utils'

const WS_KEY = 'ugc.workspace'

export function App() {
  const [signedIn, setSignedIn] = useState(() => !!tokens.access())
  const [checkingSession, setCheckingSession] = useState(true)
  useEffect(() => { refreshTokens().then(ok => { setSignedIn(ok); setCheckingSession(false) }) }, [])
  const qc = useQueryClient()
	const phoneToken = window.location.pathname.startsWith('/phone-upload/')
		? window.location.pathname.slice('/phone-upload/'.length)
		: ''

  useEffect(() => {
    const on = () => { setSignedIn(false); qc.clear() }
    window.addEventListener('reelworks:signed-out', on)
    return () => window.removeEventListener('reelworks:signed-out', on)
  }, [qc])

  if (phoneToken) return <PhoneUpload token={phoneToken} />

  if (checkingSession) return <div className="p-8">Checking your session…</div>

  if (window.location.pathname === '/accept-invitation') return <InvitationAcceptance signedIn={signedIn} />
  if (window.location.pathname === '/reset-password') return <PasswordRecovery reset />
  if (window.location.pathname === '/forgot-password') return <PasswordRecovery />
  if (!signedIn) {
    return (
      <Login
        ownerSetup={window.location.pathname === '/internal/owner-setup'}
        onSignedIn={() => {
          if (window.location.pathname === '/internal/owner-setup') {
            window.history.replaceState(null, '', '/')
          }
          window.location.hash = '#/'
          setSignedIn(true)
        }}
      />
    )
  }
  if (window.location.pathname === '/account') return <div className="mx-auto max-w-3xl p-6"><a href="/" className="mb-5 block underline">Back to workspace</a><AccountSecurity /></div>
  return <Shell onSignOut={() => { api.post('/api/auth/logout').then(() => { tokens.clear(); qc.clear(); setSignedIn(false) }).catch(() => { window.alert('Could not sign out. Please try again.') }) }} />
}

type NavItem = { to: Route; label: string; icon: React.ComponentType<{ className?: string }> }

const MAIN: NavItem[] = [
  { to: { name: 'getting-started' }, label: 'Getting started', icon: ListChecks },
  { to: { name: 'jobs' }, label: 'Videos', icon: Clapperboard },
]

// Grouped rather than nested behind a Settings page: these are configured
// occasionally, but when you do want one you want it in a single click, not
// two. The heading is what marks them as setup.
const SETUP: NavItem[] = [
  { to: { name: 'settings', section: 'avatars' }, label: 'Avatars', icon: UserSquare2 },
  { to: { name: 'settings', section: 'assets' }, label: 'Assets', icon: Film },
  { to: { name: 'settings', section: 'workspace' }, label: 'Settings', icon: SlidersHorizontal },
]

function Shell({ onSignOut }: { onSignOut: () => void }) {
  const route = useRoute()
  const [navOpen, setNavOpen] = useState(false)

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ user: User; workspaces: Workspace[] }>('/api/auth/me'),
  })

  const spaces = me.data?.workspaces ?? []
  const [wsID, setWsID] = useState(() => localStorage.getItem(WS_KEY) ?? '')
  const routed = route.workspace ? spaces.find(w => w.slug === route.workspace) : undefined
  const current = routed ?? spaces.find(w => w.id === wsID) ?? spaces[0]

  if (current) setActiveWorkspaceRoute(current.slug)

  useEffect(() => {
    if (current && current.id !== wsID) {
      setWsID(current.id)
      localStorage.setItem(WS_KEY, current.id)
    }
  }, [current, wsID])

  useEffect(() => {
    if (!current || route.name === 'new-workspace' || route.workspace === current.slug) return
    window.history.replaceState(null, '', href({ ...route, workspace: current.slug }))
  }, [current, route])

  const role = useQuery({
    queryKey: ['role', current?.id],
    queryFn: () => api.get<{ role: Role }>(`/api/workspaces/${current!.id}/me`),
    enabled: !!current,
  })

  // Close the drawer on navigation, or a phone user taps a link and stares at
  // the menu they just used.
  useEffect(() => { setNavOpen(false) }, [route])

  if (me.isLoading) return <BootSkeleton />
  if (me.isError) {
    return (
      <Centered>
        <p className="text-bad">Could not load your account.</p>
        <Button onClick={onSignOut}>Sign out</Button>
      </Centered>
    )
  }
  if (!current) {
    if (route.name === 'new-workspace') {
      return (
        <div className="min-h-screen px-5 py-6">
          <Wordmark />
          <WorkspaceOnboarding
            onCreated={id => { setWsID(id); localStorage.setItem(WS_KEY, id) }}
          />
        </div>
      )
    }
    return (
      <Centered>
        <h2 className="text-xl font-semibold">No workspace access</h2>
        <a href="/account" className="underline">Manage your account</a>
        <p className="max-w-sm text-sm text-ink-faint">
          Your account has no workspace access. Ask an admin to invite you.
        </p>
        <Button onClick={onSignOut}>Sign out</Button>
      </Centered>
    )
  }

  const ws = current.id
  const myRole = role.data?.role

  if (route.name === 'new-workspace') {
    return (
      <div className="min-h-screen px-5 py-6">
        <Wordmark />
        <WorkspaceOnboarding
          onCreated={id => { setWsID(id); localStorage.setItem(WS_KEY, id) }}
        />
      </div>
    )
  }

  const sidebar = (
    <div className="flex h-full w-60 shrink-0 flex-col border-r border-line bg-surface-1/60">
      <div className="flex h-14 items-center justify-between px-4">
        <Wordmark />
        <Button
          variant="ghost" size="icon" className="lg:hidden"
          onClick={() => setNavOpen(false)} aria-label="Close menu"
        >
          <X />
        </Button>
      </div>

      <div className="px-3 pb-3">
        <label className="mb-1.5 block px-1 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
          Workspace
        </label>
        <Select
          size="sm"
          ariaLabel="Workspace"
          value={ws}
          onValueChange={v => {
            if (v === '__new_workspace__') {
              go({ name: 'new-workspace' })
              return
            }
            const next = spaces.find(workspace => workspace.id === v)
            setWsID(v)
            localStorage.setItem(WS_KEY, v)
            if (next) {
              setActiveWorkspaceRoute(next.slug)
              go({ name: 'jobs', workspace: next.slug })
            }
          }}
          items={[
            ...spaces.map(w => ({ value: w.id, label: w.name })),
            ...(myRole === 'owner'
              ? [{ value: '__new_workspace__', label: '＋ New workspace' }]
              : []),
          ]}
        />
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-2">
        <div className="space-y-0.5">
          {MAIN.map(i => <SideLink key={i.label} item={i} route={route} />)}
        </div>
        <div className="space-y-0.5">
          <div className="px-3 pb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            Setup
          </div>
          {SETUP.map(i => <SideLink key={i.label} item={i} route={route} />)}
        </div>
      </nav>

      <div className="border-t border-line p-3">
        <div className="flex items-center gap-2">
          <div className="grid size-8 shrink-0 place-items-center rounded-full bg-surface-3 text-xs font-medium text-ink-dim">
            {me.data?.user.email.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-[13px] text-ink" title={me.data?.user.email}>
              {me.data?.user.email}
            </div>
            {myRole && (
              <div className="text-[11px] uppercase tracking-wide text-ink-faint">{myRole}</div>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onSignOut} title="Sign out">
            <LogOut />
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen lg:flex">{sidebar}</aside>

      {navOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setNavOpen(false)} />
          <div className="relative h-full bg-surface-0">{sidebar}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-surface-0/80 px-5 backdrop-blur-xl">
          <Button
            variant="ghost" size="icon" className="lg:hidden"
            onClick={() => setNavOpen(true)} aria-label="Open menu"
          >
            <Menu />
          </Button>
          <span className="text-sm font-medium text-ink-dim lg:hidden">ReelWorks</span>
          <div className="flex-1" />
          <TaskIndicator ws={ws} />
        </header>

        {/* Every page renders here, so the trailing space is set once. Without
            it the last card ends flush against the window edge, which reads as
            content cut off rather than content finished -- and leaves nothing
            to grab when a dropdown or a confirm opens near the bottom. */}
        <main className="mx-auto w-full max-w-5xl flex-1 px-5 pb-24 pt-8">
          {route.name === 'jobs' && <Jobs ws={ws} role={myRole} />}
          {route.name === 'getting-started' && <GettingStarted ws={ws} role={myRole} />}
          {route.name === 'job' && <JobDetail ws={ws} id={route.id} role={myRole} />}
          {route.name === 'new-job' && <NewJob ws={ws} sourceID={route.sourceID} />}
          {route.name === 'settings' && (
            <Settings ws={ws} section={route.section} sub={route.sub} role={myRole} />
          )}
        </main>
      </div>
    </div>
  )
}

function Wordmark() {
  return (
    <a
      href="#/"
      onClick={e => { e.preventDefault(); go({ name: 'jobs' }) }}
      className="flex shrink-0 items-center gap-2"
    >
      <span className="grid size-7 place-items-center rounded-lg bg-brand text-brand-ink shadow-[0_2px_10px_oklch(0.78_0.155_72/0.35)]">
        <Sparkles className="size-4" />
      </span>
      <span className="text-[15px] font-semibold tracking-tight">ReelWorks</span>
    </a>
  )
}

function SideLink({ item, route }: { item: NavItem; route: Route }) {
  const active =
    item.to.name === route.name &&
    (item.to.name !== 'settings' ||
      (route.name === 'settings' && item.to.section === route.section))
  // An ad's detail page still belongs to Ads.
  const alsoActive = item.to.name === 'jobs' && route.name === 'job'
  const containsTemplates = item.to.name === 'settings'
    && item.to.section === 'assets'
    && route.name === 'settings'
    && route.section === 'templates'
  const on = active || alsoActive || containsTemplates
  const Icon = item.icon

  return (
    <a
      href={href(item.to)}
      onClick={e => { e.preventDefault(); go(item.to) }}
      className={cn(
        'flex items-center gap-2.5 rounded-[--radius-control] px-3 py-2 text-sm font-medium transition-colors',
        on
          ? 'bg-surface-3 text-ink shadow-[inset_2px_0_0_var(--color-brand)]'
          : 'text-ink-dim hover:bg-surface-2 hover:text-ink',
      )}
    >
      <Icon className={cn('size-4', on ? 'text-brand' : 'text-ink-faint')} />
      {item.label}
    </a>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center px-5">
      <div className="flex flex-col items-center gap-4 text-center">{children}</div>
    </div>
  )
}

function BootSkeleton() {
  return (
    <div className="flex min-h-screen">
      <div className="hidden w-60 border-r border-line p-4 lg:block">
        <Skeleton className="h-7 w-32" />
        <Skeleton className="mt-6 h-8 w-full" />
        <div className="mt-6 space-y-2">
          {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      </div>
      <div className="flex-1 space-y-4 px-5 py-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    </div>
  )
}
